import React, { useState, useEffect } from 'react';
import { database, ref as dbRef, onValue, update, set, push, get } from '../firebase';
import { storage, storageRef, uploadBytes, getDownloadURL } from '../firebase';
import { FaImage, FaAd, FaSpinner, FaCheck, FaEdit, FaTimes, FaPlus, FaCrown, FaCalendarAlt, FaInfoCircle } from 'react-icons/fa';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import gpayImg from '../img/gpay.png';
import phonepeImg from '../img/phonepe.svg';

const Instruction = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    information: '',
    infoImages: [], // array of File objects
    adImage: null,
    infoImagePreviews: [], // array of preview URLs
    adImagePreview: '',
  });
  const [subscriptionForm, setSubscriptionForm] = useState({
    yearlyPrice: '',
    unlimitedPrice: '',
  });
  const [paymentForm, setPaymentForm] = useState({
    gpayNumber: '',
    phonepeNumber: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscriptionSubmitting, setIsSubscriptionSubmitting] = useState(false);
  const [instructions, setInstructions] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(false);
  const [modal, setModal] = useState({ show: false, message: '', type: '' });
  const [showForm, setShowForm] = useState(false);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const formRef = React.useRef(null);
  const [legalForm, setLegalForm] = useState({
    legalText: '',
    legalImage: null,
    legalImagePreview: '',
  });
  const [isLegalSubmitting, setIsLegalSubmitting] = useState(false);
  const [legalInfo, setLegalInfo] = useState(null);
  const [isEditingLegal, setIsEditingLegal] = useState(false);
  const [heading, setHeading] = useState('');
  const [headingInput, setHeadingInput] = useState('');
  const [isHeadingLoading, setIsHeadingLoading] = useState(true);
  const [isEditingHeading, setIsEditingHeading] = useState(false);
  const [isHeadingSubmitting, setIsHeadingSubmitting] = useState(false);

  // Fetch instructions from Firebase on mount
  useEffect(() => {
    const instructionsRef = dbRef(database, 'videoplayer/Information');
    onValue(instructionsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const instructionList = Object.entries(data)
          .map(([id, value]) => ({
            id,
            ...value,
          }))
          .sort((a, b) => {
            // Sort by numeric ID if possible, otherwise by string
            const aNum = parseInt(a.id);
            const bNum = parseInt(b.id);
            if (!isNaN(aNum) && !isNaN(bNum)) {
              return aNum - bNum;
            }
            return a.id.localeCompare(b.id);
          });
        setInstructions(instructionList);
      } else {
        setInstructions([]);
      }
    });

    // Fetch subscription plans
    const subscriptionRef = dbRef(database, 'videoplayer/SubscriptionPlans');
    onValue(subscriptionRef, (snapshot) => {
      const data = snapshot.val();
      setSubscriptionPlans(data);
    });

    // Fetch payment information
    const paymentRef = dbRef(database, 'videoplayer/SubscriptionPlans/payment');
    onValue(paymentRef, (snapshot) => {
      const data = snapshot.val();
      setPaymentInfo(data);
    });
  }, []);

  // Fetch legal info on mount
  useEffect(() => {
    const legalRef = dbRef(database, 'videoplayer/legal');
    onValue(legalRef, (snapshot) => {
      const data = snapshot.val();
      setLegalInfo(data);
    });
  }, []);

  // Fetch heading from Firebase on mount
  useEffect(() => {
    const headingRef = dbRef(database, 'videoplayer/heading');
    onValue(headingRef, (snapshot) => {
      const data = snapshot.val();
      setHeading(data || '');
      setIsHeadingLoading(false);
      setIsEditingHeading(false);
      setHeadingInput('');
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubscriptionChange = (e) => {
    const { name, value } = e.target;
    // Only allow digits (no decimals, no minus, no e)
    const numericValue = value.replace(/[^\d]/g, '');
    setSubscriptionForm((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
  };

  // Prevent non-numeric input for price fields
  const handlePriceKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrows
    if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
      return;
    }
    // Prevent: anything that's not a digit
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Prevent pasting non-numeric
  const handlePricePaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData('text');
    if (!/^\d+$/.test(paste)) {
      e.preventDefault();
    }
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    // Only allow numbers, max 10 digits
    const numericValue = value.replace(/[^\d]/g, '').slice(0, 10);
    setPaymentForm((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
  };

  const handleImageChange = (e) => {
    const { name, files } = e.target;
    if (name === 'infoImage') {
      // Multiple info images
      const newFiles = Array.from(files);
      setFormData((prev) => ({
        ...prev,
        infoImages: [...prev.infoImages, ...newFiles],
        infoImagePreviews: [
          ...prev.infoImagePreviews,
          ...newFiles.map((file) => URL.createObjectURL(file)),
        ],
      }));
    } else {
      // Single ad image
      const file = files[0];
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setFormData((prev) => ({
          ...prev,
          adImage: file,
          adImagePreview: previewUrl,
        }));
      }
    }
  };

  const handleRemoveInfoImage = (index) => {
    setFormData((prev) => {
      const newImages = [...prev.infoImages];
      const newPreviews = [...prev.infoImagePreviews];
      newImages.splice(index, 1);
      newPreviews.splice(index, 1);
      return {
        ...prev,
        infoImages: newImages,
        infoImagePreviews: newPreviews,
      };
    });
  };

  const handleLegalChange = (e) => {
    const { name, value } = e.target;
    setLegalForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLegalImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLegalForm((prev) => ({
        ...prev,
        legalImage: file,
        legalImagePreview: previewUrl,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let infoImageUrls = [];
      let adImageUrl = formData.adImagePreview;

      // Upload info images if present
      if (formData.infoImages.length > 0) {
        // Use sequential names for each image
        infoImageUrls = await Promise.all(
          formData.infoImages.map((file, idx) => {
            const infoImageRef = storageRef(
              storage,
              `videoplayer/Information/information_image_${idx + 1}`
            );
            return uploadBytes(infoImageRef, file).then((snapshot) => getDownloadURL(snapshot.ref));
          })
        );
      } else if (formData.infoImagePreviews.length > 0) {
        // If editing and no new images, keep previews (should be URLs)
        infoImageUrls = formData.infoImagePreviews;
      }

      // Upload ad image if present
      if (formData.adImage) {
        const adImageRef = storageRef(storage, 'videoplayer/Information/advertisement_image');
        const adSnapshot = await uploadBytes(adImageRef, formData.adImage);
        adImageUrl = await getDownloadURL(adSnapshot.ref);
      }

      if (editingId) {
        // Update existing instruction
        const instructionRef = dbRef(database, `videoplayer/Information/${editingId}`);
        await update(instructionRef, {
          information: formData.information,
          infoImageUrls, // array of URLs
          adImageUrl,
          updatedAt: Date.now(),
        });
        
        setModal({ show: true, message: 'Instruction updated successfully!', type: 'success' });
      } else {
        // Create new instruction with sequential ID
        const instructionsRef = dbRef(database, 'videoplayer/Information');
        const snapshot = await get(instructionsRef);
        let nextId = 1;
        
        if (snapshot.exists()) {
          const existingInstructions = Object.keys(snapshot.val());
          nextId = Math.max(...existingInstructions.map(id => parseInt(id) || 0)) + 1;
        }
        
        const newInstructionData = {
          information: formData.information,
          infoImageUrls, // array of URLs
          adImageUrl,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        
        const newInstructionRef = dbRef(database, `videoplayer/Information/${nextId}`);
        await set(newInstructionRef, newInstructionData);
        
        setModal({ show: true, message: 'Instruction created successfully!', type: 'success' });
      }
      
      // Reset form and close editing
      setFormData({
        information: '',
        infoImages: [],
        adImage: null,
        infoImagePreviews: [],
        adImagePreview: '',
      });
      setEditingId(null);
      setShowForm(false);
    } catch (error) {
      // console.error('Error updating instruction:', error);
      setModal({ show: true, message: 'Error updating instruction. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    setIsSubscriptionSubmitting(true);

    try {
      // Extract numeric values from price strings
      const yearlyPrice = parseInt(subscriptionForm.yearlyPrice.replace(/[^\d]/g, '')) || 0;
      const unlimitedPrice = parseInt(subscriptionForm.unlimitedPrice.replace(/[^\d]/g, '')) || 0;

      const subscriptionData = {
        yearly: {
          price: yearlyPrice,
        },
        unlimited: {
          price: unlimitedPrice,
        },
        updatedAt: Date.now(),
      };

      if (editingSubscription) {
        // Update existing subscription plans
        const subscriptionRef = dbRef(database, 'videoplayer/SubscriptionPlans');
        await update(subscriptionRef, subscriptionData);
        setModal({ show: true, message: 'Subscription plans updated successfully!', type: 'success' });
      } else {
        // Create new subscription plans
        const subscriptionRef = dbRef(database, 'videoplayer/SubscriptionPlans');
        await set(subscriptionRef, subscriptionData);
        setModal({ show: true, message: 'Subscription plans created successfully!', type: 'success' });
      }

      // Reset form
      setSubscriptionForm({
        yearlyPrice: '',
        unlimitedPrice: '',
      });
      setEditingSubscription(false);
      setShowSubscriptionForm(false);
    } catch (error) {
      // console.error('Error saving subscription plans:', error);
      setModal({ show: true, message: 'Error saving subscription plans. Please try again.', type: 'error' });
    } finally {
      setIsSubscriptionSubmitting(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setIsSubscriptionSubmitting(true);

    try {
      const paymentData = {
        gpayNumber: paymentForm.gpayNumber,
        phonepeNumber: paymentForm.phonepeNumber,
        updatedAt: Date.now(),
      };

      // Save payment information to Firebase
      const paymentRef = dbRef(database, 'videoplayer/SubscriptionPlans/payment');
      await set(paymentRef, paymentData);
      
      setModal({ show: true, message: 'Payment information saved successfully!', type: 'success' });
      
      // Reset form
      setPaymentForm({
        gpayNumber: '',
        phonepeNumber: '',
      });
      setShowPaymentForm(false);
    } catch (error) {
      // console.error('Error saving subscription plans:', error);
      setModal({ show: true, message: 'Error saving subscription plans. Please try again.', type: 'error' });
    } finally {
      setIsSubscriptionSubmitting(false);
    }
  };

  const handleLegalSubmit = async (e) => {
    e.preventDefault();
    setIsLegalSubmitting(true);
    try {
      let legalImageUrl = legalInfo?.legalImageUrl || '';
      if (legalForm.legalImage) {
        const legalImageRef = storageRef(storage, 'videoplayer/legal/legal');
        const legalSnapshot = await uploadBytes(legalImageRef, legalForm.legalImage);
        legalImageUrl = await getDownloadURL(legalSnapshot.ref);
      }
      const legalData = {
        legalText: legalForm.legalText,
        legalImageUrl,
        updatedAt: Date.now(),
      };
      const legalRef = dbRef(database, 'videoplayer/legal');
      await set(legalRef, legalData);
      setModal({ show: true, message: 'Legal information updated successfully!', type: 'success' });
      setLegalForm({ legalText: '', legalImage: null, legalImagePreview: '' });
      setIsEditingLegal(false);
    } catch (error) {
      setModal({ show: true, message: 'Error updating legal information. Please try again.', type: 'error' });
    } finally {
      setIsLegalSubmitting(false);
    }
  };

  const handleEdit = (instruction) => {
    setFormData({
      information: instruction.information,
      infoImages: [], // don't preload files
      adImage: null,
      infoImagePreviews: instruction.infoImageUrls || [], // array of URLs
      adImagePreview: instruction.adImageUrl,
    });
    setEditingId(instruction.id);
    setShowForm(true);
    // Scroll to form after a short delay to ensure it's rendered
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleEditSubscription = () => {
    if (subscriptionPlans) {
      setSubscriptionForm({
        yearlyPrice: subscriptionPlans.yearly?.price ? `₹${subscriptionPlans.yearly.price}` : '',
        unlimitedPrice: subscriptionPlans.unlimited?.price ? `₹${subscriptionPlans.unlimited.price}` : '',
      });
      setEditingSubscription(true);
    } else {
      setSubscriptionForm({
        yearlyPrice: '',
        unlimitedPrice: '',
      });
      setEditingSubscription(false);
    }
    setShowSubscriptionForm(true);
  };

  const handleCancelEdit = () => {
    setFormData({
      information: '',
      infoImages: [],
      adImage: null,
      infoImagePreviews: [],
      adImagePreview: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleCancelSubscriptionEdit = () => {
    setSubscriptionForm({
      yearlyPrice: '',
      unlimitedPrice: '',
    });
    setEditingSubscription(false);
    setShowSubscriptionForm(false);
  };

  const handleCancelPaymentEdit = () => {
    setPaymentForm({
      gpayNumber: '',
      phonepeNumber: '',
    });
    setShowPaymentForm(false);
  };

  const handleEditLegal = () => {
    setLegalForm({
      legalText: legalInfo?.legalText || '',
      legalImage: null,
      legalImagePreview: legalInfo?.legalImageUrl || '',
    });
    setIsEditingLegal(true);
  };

  const handleCancelLegalEdit = () => {
    setLegalForm({ legalText: '', legalImage: null, legalImagePreview: '' });
    setIsEditingLegal(false);
  };

  const handleHeadingInputChange = (e) => {
    setHeadingInput(e.target.value);
  };

  const handleHeadingSubmit = async (e) => {
    e.preventDefault();
    if (!headingInput.trim()) return;
    setIsHeadingSubmitting(true);
    try {
      const headingRef = dbRef(database, 'videoplayer/heading');
      await set(headingRef, headingInput.trim());
      setModal({ show: true, message: 'Heading saved successfully!', type: 'success' });
      setIsEditingHeading(false);
    } catch (error) {
      setModal({ show: true, message: 'Error saving heading. Please try again.', type: 'error' });
    } finally {
      setIsHeadingSubmitting(false);
    }
  };

  const handleEditHeading = () => {
    setHeadingInput(heading);
    setIsEditingHeading(true);
  };

  const handleCancelHeadingEdit = () => {
    setIsEditingHeading(false);
    setHeadingInput('');
  };

  const closeModal = () => {
    setModal({ show: false, message: '', type: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-gray-100 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Bar */}
        <header className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 shadow-md rounded-xl mb-4 sm:mb-6 lg:mb-8 px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white hover:text-indigo-200 transition-colors text-base sm:text-lg focus:outline-none"
            title="Back"
          >
            <FiArrowLeft className="mr-2 text-xl sm:text-2xl" />
          </button>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
            Admin Dashboard
          </h1>
        </header>

        {/* Heading Section */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaInfoCircle className="text-blue-600" />
              Heading
            </h2>
            {isHeadingLoading ? (
              <div className="text-gray-500">Loading...</div>
            ) : heading && !isEditingHeading ? (
              <div className="flex flex-col gap-4">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-xl shadow-sm">
                  <span className="text-lg text-gray-800 font-semibold">{heading}</span>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleEditHeading}
                    className="inline-flex items-center justify-center px-5 py-2 rounded-lg text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-md transition duration-200 min-h-[44px]"
                  >
                    <FaEdit className="mr-2" />
                    Edit
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleHeadingSubmit} className="space-y-4">
                <div>
                  <label htmlFor="headingInput" className="block text-base font-semibold text-gray-700 mb-2">Heading</label>
                  <textarea
                    id="headingInput"
                    value={headingInput}
                    onChange={handleHeadingInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-gray-50 resize-y min-h-[80px]"
                    placeholder="Enter heading..."
                    required
                    maxLength={500}
                    rows={3}
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    type="submit"
                    disabled={isHeadingSubmitting}
                    className={`inline-flex items-center justify-center px-6 py-3 rounded-lg text-base font-semibold text-white shadow-md transition duration-200 min-h-[44px] ${isHeadingSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    {isHeadingSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        {isEditingHeading ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        <FaCheck className="mr-2" />
                        {isEditingHeading ? 'Update' : 'Submit'}
                      </>
                    )}
                  </button>
                  {isEditingHeading && (
                    <button
                      type="button"
                      onClick={handleCancelHeadingEdit}
                      className="px-6 py-3 rounded-lg text-base font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition duration-200 min-h-[44px]"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Edit Form - Only shown when editing */}
        {showForm && (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="space-y-4 sm:space-y-6 bg-white p-4 sm:p-6 lg:p-8 rounded-xl shadow-lg border border-gray-200 max-w-3xl mx-auto mb-4 sm:mb-6 lg:mb-8"
          >
            <div className="flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                {editingId ? `Edit Instruction #${editingId}` : 'Add New Instruction'}
              </h2>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="text-gray-500 hover:text-gray-700 transition duration-200 p-2 rounded-lg hover:bg-gray-100"
              >
                <FaTimes className="text-lg sm:text-xl" />
              </button>
            </div>

            {/* Information Text */}
            <div>
              <label
                htmlFor="information"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Instruction Information
              </label>
              <textarea
                id="information"
                name="information"
                rows={20}
                className="w-full p-3 sm:p-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 resize-y min-h-[120px]"
                placeholder="Enter instruction details..."
                value={formData.information}
                onChange={handleChange}
                required
              />
            </div>

            {/* Information Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Information Images
              </label>
              <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                <label className="cursor-pointer bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center justify-center text-sm w-full sm:w-auto min-h-[44px]">
                  <FaImage className="mr-2 text-base" />
                  Upload Information Images
                  <input
                    id="infoImage"
                    name="infoImage"
                    type="file"
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                  />
                </label>
                <button
                  type="button"
                  className="ml-2 bg-green-500 hover:bg-green-600 text-white rounded-full p-2 flex items-center justify-center"
                  onClick={() => document.getElementById('infoImage').click()}
                  title="Add another image"
                >
                  <FaPlus />
                </button>
                <span className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">or drag and drop</span>
              </div>
              {formData.infoImagePreviews && formData.infoImagePreviews.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {formData.infoImagePreviews.map((preview, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={preview}
                        alt={`Information preview ${idx + 1}`}
                        className="h-24 sm:h-32 w-full object-cover rounded-lg shadow-sm"
                      />
                      <button
                        type="button"
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-80 hover:opacity-100"
                        onClick={() => handleRemoveInfoImage(idx)}
                        title="Remove image"
                      >
                        <FaTimes />
                      </button>
                      <span className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-2 py-0.5 rounded">{idx + 1}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Advertisement Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Advertisement Image
              </label>
              <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                <label className="cursor-pointer bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center justify-center text-sm w-full sm:w-auto min-h-[44px]">
                  <FaAd className="mr-2 text-base" />
                  Upload Ad Image
                  <input
                    id="adImage"
                    name="adImage"
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </label>
                <span className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">or drag and drop</span>
              </div>
              {formData.adImagePreview && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500 mb-2">Preview:</p>
                  <img
                    src={formData.adImagePreview}
                    alt="Advertisement preview"
                    className="h-32 sm:h-40 lg:h-48 w-full object-cover rounded-lg shadow-sm"
                  />
                </div>
              )}
            </div>

            {/* Update Button */}
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-medium text-white shadow-md transition duration-200 w-full sm:w-auto min-h-[48px] ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    {editingId ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <FaCheck className="mr-2" />
                    {editingId ? 'Update Instruction' : 'Create Instruction'}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-6 py-3 rounded-lg text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition duration-200 w-full sm:w-auto min-h-[48px]"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Payment Form - Only shown when editing */}
        {showPaymentForm && (
          <form
            onSubmit={handlePaymentSubmit}
            className="space-y-4 sm:space-y-6 bg-white p-4 sm:p-6 lg:p-8 rounded-xl shadow-lg border border-gray-200 max-w-2xl mx-auto mb-4 sm:mb-6 lg:mb-8"
          >
            <div className="flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                Payment Information
              </h2>
              <button
                type="button"
                onClick={handleCancelPaymentEdit}
                className="text-gray-500 hover:text-gray-700 transition duration-200 p-2 rounded-lg hover:bg-gray-100"
              >
                <FaTimes className="text-lg sm:text-xl" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              {/* GPay Number */}
              <div className="space-y-4">
                <div className="flex items-center mb-2">
                  <img src={gpayImg} alt="GPay" className="h-7 w-7 mr-2" />
                  <h3 className="text-lg font-medium text-gray-800">GPay Number</h3>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="gpayNumber"
                    value={paymentForm.gpayNumber}
                    onChange={handlePaymentChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                    placeholder="e.g., 9876543210"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    required
                    inputMode="numeric"
                  />
                </div>
              </div>

              {/* PhonePe Number */}
              <div className="space-y-4">
                <div className="flex items-center mb-2">
                  <img src={phonepeImg} alt="PhonePe" className="h-7 w-7 mr-2" />
                  <h3 className="text-lg font-medium text-gray-800">PhonePe Number</h3>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phonepeNumber"
                    value={paymentForm.phonepeNumber}
                    onChange={handlePaymentChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                    placeholder="e.g., 9876543210"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    required
                    inputMode="numeric"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                type="submit"
                disabled={isSubscriptionSubmitting}
                className={`inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-medium text-white shadow-md transition duration-200 w-full sm:w-auto min-h-[48px] ${
                  isSubscriptionSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                {isSubscriptionSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaCheck className="mr-2" />
                    Save Payment Info
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancelPaymentEdit}
                className="px-6 py-3 rounded-lg text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition duration-200 w-full sm:w-auto min-h-[48px]"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Subscription Form - Only shown when editing */}
        {showSubscriptionForm && (
          <form
            onSubmit={handleSubscriptionSubmit}
            className="space-y-4 sm:space-y-6 bg-white p-4 sm:p-6 lg:p-8 rounded-xl shadow-lg border border-gray-200 max-w-2xl mx-auto mb-4 sm:mb-6 lg:mb-8"
          >
            <div className="flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                {editingSubscription ? 'Edit Subscription Plans' : 'Create Subscription Plans'}
              </h2>
              <button
                type="button"
                onClick={handleCancelSubscriptionEdit}
                className="text-gray-500 hover:text-gray-700 transition duration-200 p-2 rounded-lg hover:bg-gray-100"
              >
                <FaTimes className="text-lg sm:text-xl" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              {/* Yearly Plan */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-800 flex items-center">
                  <FaCalendarAlt className="mr-2 text-blue-600" />
                  Yearly Plan
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xl text-gray-700">₹</span>
                    <input
                      type="text"
                      name="yearlyPrice"
                      value={subscriptionForm.yearlyPrice}
                      onChange={handleSubscriptionChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                      placeholder="999"
                      required
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onKeyDown={handlePriceKeyDown}
                      onPaste={handlePricePaste}
                    />
                  </div>
                  {subscriptionForm.yearlyPrice && (
                    <div className="mt-1 text-sm text-gray-600">Entered: <span className="font-semibold text-blue-700">₹{subscriptionForm.yearlyPrice}</span></div>
                  )}
                </div>
              </div>

              {/* Unlimited Plan */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-800 flex items-center">
                  <FaCrown className="mr-2 text-yellow-600" />
                  Unlimited Plan
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xl text-gray-700">₹</span>
                    <input
                      type="text"
                      name="unlimitedPrice"
                      value={subscriptionForm.unlimitedPrice}
                      onChange={handleSubscriptionChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                      placeholder="1999"
                      required
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onKeyDown={handlePriceKeyDown}
                      onPaste={handlePricePaste}
                    />
                  </div>
                  {subscriptionForm.unlimitedPrice && (
                    <div className="mt-1 text-sm text-gray-600">Entered: <span className="font-semibold text-blue-700">₹{subscriptionForm.unlimitedPrice}</span></div>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                type="submit"
                disabled={isSubscriptionSubmitting}
                className={`inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-medium text-white shadow-md transition duration-200 w-full sm:w-auto min-h-[48px] ${
                  isSubscriptionSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                {isSubscriptionSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaCheck className="mr-2" />
                    {editingSubscription ? 'Update Plans' : 'Create Plans'}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancelSubscriptionEdit}
                className="px-6 py-3 rounded-lg text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition duration-200 w-full sm:w-auto min-h-[48px]"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Modal for Success/Error Messages */}
        {modal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3 sm:px-4">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl max-w-sm sm:max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3
                  className={`text-base sm:text-lg font-semibold ${
                    modal.type === 'success' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {modal.type === 'success' ? 'Success' : 'Error'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 transition duration-200 p-1"
                >
                  <FaTimes className="text-lg sm:text-xl" />
                </button>
              </div>
              <p className="text-gray-600 text-sm sm:text-base">{modal.message}</p>
              <div className="mt-4 sm:mt-6 flex justify-end">
                <button
                  onClick={closeModal}
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 text-sm w-full sm:w-auto"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Plans Section */}
        <div className="mb-6 sm:mb-8 lg:mb-12">
          <div className="flex flex-col gap-4 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 text-center sm:text-left">Subscription Plans</h2>
            <div className="flex flex-row gap-2 w-full">
              <button
                onClick={handleEditSubscription}
                className="flex-1 min-w-[120px] inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 text-sm font-medium min-h-[44px]"
               >
                <FaEdit className="mr-2" />
                {subscriptionPlans ? 'Edit Plans' : 'Create Plans'}
              </button>
              <button
                onClick={() => {
                  if (paymentInfo) {
                    setPaymentForm({
                      gpayNumber: paymentInfo.gpayNumber || '',
                      phonepeNumber: paymentInfo.phonepeNumber || '',
                    });
                  } else {
                    setPaymentForm({
                      gpayNumber: '',
                      phonepeNumber: '',
                    });
                  }
                  setShowPaymentForm(true);
                }}
                className="flex-1 min-w-[120px] inline-flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 text-sm font-medium min-h-[44px]"
              >
                <FaAd className="mr-2" />
                {paymentInfo ? 'Edit Payment Info' : 'Add Payment Info'}
              </button>
            </div>
          </div>

          {subscriptionPlans ? (
            <div className="grid grid-cols-1 gap-4">
              {/* Yearly Plan Card */}
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-200">
                <div className="flex items-center mb-3">
                  <FaCalendarAlt className="text-blue-600 text-lg sm:text-xl mr-3" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800">Yearly Plan</h3>
                </div>
                <div className="mb-3">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">₹{subscriptionPlans.yearly?.price}</p>
                </div>
              </div>

              {/* Unlimited Plan Card */}
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-200">
                <div className="flex items-center mb-3">
                  <FaCrown className="text-yellow-600 text-lg sm:text-xl mr-3" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800">Unlimited Plan</h3>
                </div>
                <div className="mb-3">
                  <p className="text-xl sm:text-2xl font-bold text-yellow-600">₹{subscriptionPlans.unlimited?.price}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 bg-white rounded-xl shadow-lg border border-gray-200">
              <FaCrown className="text-gray-400 text-3xl sm:text-4xl mx-auto mb-3 sm:mb-4" />
              <p className="text-gray-500 text-sm sm:text-base">No subscription plans created yet</p>
              <button
                onClick={handleEditSubscription}
                className="mt-3 sm:mt-4 inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 text-sm w-full sm:w-auto min-h-[48px]"
              >
                <FaPlus className="mr-2" />
                Create Plans
              </button>
            </div>
          )}

          {/* Payment Information Display */}
          {paymentInfo && (
            <div className="mt-6 sm:mt-8">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 text-center sm:text-left">Payment Information</h3>
              <div className="grid grid-cols-1 gap-4">
                {/* GPay Card */}
                <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-200">
                  <div className="flex items-center mb-3">
                    <img src={gpayImg} alt="GPay" className="h-8 w-8 mr-3" />
                    <h4 className="text-base sm:text-lg font-semibold text-gray-800">GPay Number</h4>
                  </div>
                  <div className="mb-3">
                    <p className="text-xl sm:text-2xl font-bold text-green-600">{paymentInfo.gpayNumber}</p>
                  </div>
                </div>

                {/* PhonePe Card */}
                <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-200">
                  <div className="flex items-center mb-3">
                    <img src={phonepeImg} alt="PhonePe" className="h-8 w-8 mr-3" />
                    <h4 className="text-base sm:text-lg font-semibold text-gray-800">PhonePe Number</h4>
                  </div>
                  <div className="mb-3">
                    <p className="text-xl sm:text-2xl font-bold text-purple-600">{paymentInfo.phonepeNumber}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Display Instructions in Table */}
        <div className="mt-6 sm:mt-8 lg:mt-12 max-w-7xl mx-auto">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 lg:mb-6 text-center sm:text-left">Current Instructions</h2>

          {instructions.length > 0 ? (
            <div className="overflow-x-auto rounded-lg shadow-lg border border-gray-200">
              <table className="min-w-full bg-white">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">
                      ID
                    </th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">
                      Information
                    </th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">
                      Info Image
                    </th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">
                      Ad Image
                    </th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {instructions.map((instruction) => (
                    <tr
                      key={instruction.id}
                      className="border-t border-gray-200 hover:bg-gray-50 transition duration-200"
                    >
                      <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-600">
                        {instruction.id}
                      </td>
                      <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-600 max-w-[100px] sm:max-w-[120px] lg:max-w-xs truncate">
                        {instruction.information}
                      </td>
                      <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                        <div className="flex flex-wrap gap-1">
                          {(instruction.infoImageUrls || []).map((url, idx) => (
                            <img
                              key={idx}
                              src={url}
                              alt={`Information ${idx + 1}`}
                              className="h-12 w-12 sm:h-16 sm:w-16 lg:h-20 lg:w-20 object-cover rounded-lg shadow-sm border border-gray-200"
                              title={`Image ${idx + 1}`}
                            />
                          ))}
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                        <img
                          src={instruction.adImageUrl}
                          alt="Advertisement"
                          className="h-12 w-12 sm:h-16 sm:w-16 lg:h-20 lg:w-20 object-cover rounded-lg shadow-sm"
                        />
                      </td>
                      <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                        <button
                          onClick={() => handleEdit(instruction)}
                          className="text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1 text-xs sm:text-sm p-2 rounded-lg hover:bg-blue-50 min-h-[32px] min-w-[32px]"
                        >
                          <FaEdit className="text-sm sm:text-base" />
                          <span className="hidden sm:inline">Edit</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 bg-white rounded-xl shadow-lg border border-gray-200">
              <FaImage className="text-gray-400 text-3xl sm:text-4xl mx-auto mb-3 sm:mb-4" />
              <p className="text-gray-500 text-sm sm:text-base">No instructions available</p>
              <button
                onClick={() => {
                  setFormData({
                    information: '',
                    infoImages: [],
                    adImage: null,
                    infoImagePreviews: [],
                    adImagePreview: '',
                  });
                  setEditingId(null);
                  setShowForm(true);
                  // Scroll to form after a short delay to ensure it's rendered
                  setTimeout(() => {
                    if (formRef.current) {
                      formRef.current.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start',
                        inline: 'nearest'
                      });
                    }
                  }, 100);
                }}
                className="mt-4 inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 text-sm w-full sm:w-auto min-h-[48px]"
              >
                <FaPlus className="mr-2" />
                Add First Instruction
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Legal Information Form */}
      <div className="max-w-2xl mx-auto mt-8 mb-8">
        <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <FaInfoCircle className="text-blue-600 text-2xl" />
            <h2 className="text-2xl font-bold text-gray-800">Legal Information</h2>
          </div>

          {/* If editing, show the form. Otherwise, show the info and Edit button. */}
          {isEditingLegal ? (
            <form onSubmit={handleLegalSubmit} className="space-y-6">
              <div>
                <label htmlFor="legalText" className="block text-base font-semibold text-gray-700 mb-2">Legal Text</label>
                <textarea
                  id="legalText"
                  name="legalText"
                  rows={5}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-gray-50"
                  placeholder="Enter legal information..."
                  value={legalForm.legalText}
                  onChange={handleLegalChange}
                  required
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">Legal Image</label>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center text-base font-medium shadow-md">
                    <FaImage className="mr-2" />
                    Browse Image
                    <input
                      type="file"
                      name="legalImage"
                      accept="image/*"
                      onChange={handleLegalImageChange}
                      className="hidden"
                    />
                  </label>
                  {legalForm.legalImage && (
                    <span className="text-gray-700 text-sm truncate max-w-xs">{legalForm.legalImage.name}</span>
                  )}
                </div>
                {legalForm.legalImagePreview && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 mb-1">Preview:</p>
                    <img src={legalForm.legalImagePreview} alt="Legal Preview" className="h-40 w-full object-cover rounded-lg shadow-md border border-gray-200" />
                  </div>
                )}
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="submit"
                  disabled={isLegalSubmitting}
                  className={`inline-flex items-center justify-center px-6 py-3 rounded-lg text-base font-semibold text-white shadow-md transition duration-200 w-full sm:w-auto min-h-[48px] ${isLegalSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {isLegalSubmitting ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FaCheck className="mr-2" />
                      Update
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancelLegalEdit}
                  className="px-6 py-3 rounded-lg text-base font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition duration-200 w-full sm:w-auto min-h-[48px]"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div>
              {legalInfo ? (
                <div className="flex flex-col gap-6">
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-5 rounded-xl shadow-sm">
                    <h3 className="text-lg font-semibold text-blue-800 mb-2">Current Legal Information</h3>
                    <p className="text-gray-700 mb-3 whitespace-pre-line text-base leading-relaxed">{legalInfo.legalText}</p>
                    {legalInfo.legalImageUrl && (
                      <img src={legalInfo.legalImageUrl} alt="Legal" className="h-52 w-full object-cover rounded-lg shadow-md border border-gray-200" />
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleEditLegal}
                      className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-md transition duration-200 w-full sm:w-auto min-h-[48px]"
                    >
                      <FaEdit className="mr-2" />
                      Edit
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  <p>No legal information available.</p>
                  <button
                    type="button"
                    onClick={handleEditLegal}
                    className="mt-4 inline-flex items-center justify-center px-6 py-3 rounded-lg text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-md transition duration-200 w-full sm:w-auto min-h-[48px]"
                  >
                    <FaEdit className="mr-2" />
                    Add Legal Information
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Mobile Responsive CSS */}
      <style jsx>{`
        /* Mobile Responsive Styles */
        
        /* Extra Small Mobile (≤320px) */
        @media (max-width: 320px) {
          .min-h-screen {
            padding: 0.25rem;
          }
          
          /* Form optimization for very small screens */
          form {
            margin: 0.5rem;
            padding: 0.75rem;
          }
          
          /* Button optimization */
          button {
            min-height: 44px;
            font-size: 0.875rem;
          }
          
          .text-2xl {
            font-size: 1.25rem;
          }
          
          .text-xl {
            font-size: 1.125rem;
          }
          
          .text-lg {
            font-size: 1rem;
          }
          
          .p-4 {
            padding: 0.75rem;
          }
          
          .px-3 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          .py-4 {
            padding-top: 0.75rem;
            padding-bottom: 0.75rem;
          }
          
          /* Form inputs */
          input, textarea, select {
            font-size: 16px;
          }
          
          /* Buttons */
          button {
            font-size: 0.875rem;
            padding: 0.75rem 1rem;
          }
          
          /* Table adjustments */
          table {
            font-size: 0.7rem;
          }
          
          th, td {
            padding: 0.25rem 0.125rem;
          }
          
          /* Image sizes */
          img {
            max-width: 100%;
            height: auto;
          }
        }

        /* Small Mobile (321px - 480px) */
        @media (min-width: 321px) and (max-width: 480px) {
          .text-2xl {
            font-size: 1.5rem;
          }
          
          .text-xl {
            font-size: 1.25rem;
          }
          
          /* Form inputs */
          input, textarea, select {
            font-size: 16px;
          }
          
          /* Buttons */
          button {
            font-size: 0.875rem;
            padding: 0.75rem 1rem;
          }
          
          /* Table adjustments */
          table {
            font-size: 0.75rem;
          }
          
          th, td {
            padding: 0.375rem 0.25rem;
          }
        }

        /* Medium Mobile (481px - 640px) */
        @media (min-width: 481px) and (max-width: 640px) {
          .text-2xl {
            font-size: 1.75rem;
          }
          
          .text-xl {
            font-size: 1.5rem;
          }
          
          /* Table adjustments */
          table {
            font-size: 0.875rem;
          }
          
          th, td {
            padding: 0.5rem 0.375rem;
          }
        }

        /* Large Mobile (641px - 768px) */
        @media (min-width: 641px) and (max-width: 768px) {
          .text-2xl {
            font-size: 2rem;
          }
          
          .text-xl {
            font-size: 1.75rem;
          }
        }

        /* Standard Mobile (≤768px) */
        @media (max-width: 768px) {
          /* Optimize animations for mobile */
          .transform {
            will-change: transform;
          }
          
          /* Reduce shadow complexity on mobile */
          .shadow-lg, .shadow-xl {
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
          
          /* Optimize table rendering */
          table {
            contain: layout style paint;
          }
          
          /* Reduce gradient complexity */
          .bg-gradient-to-b {
            background: linear-gradient(to bottom, var(--tw-gradient-stops));
          }
          
          /* Better touch targets */
          button, input, select, textarea {
            min-height: 44px;
            min-width: 44px;
          }
          
          /* Touch feedback */
          button:active, input:active, select:active {
            transform: scale(0.98);
            transition: transform 0.1s ease;
          }
        }

        /* Landscape Mobile */
        @media (max-width: 768px) and (orientation: landscape) {
          .min-h-screen {
            padding: 0.75rem;
          }
          
          .text-2xl {
            font-size: 1.5rem;
          }
          
          .text-xl {
            font-size: 1.25rem;
          }
          
          /* Reduce vertical spacing */
          .mb-4 {
            margin-bottom: 0.5rem;
          }
          
          .mb-6 {
            margin-bottom: 1rem;
          }
          
          .mb-8 {
            margin-bottom: 1.5rem;
          }
          
          .py-4 {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
          }
          
          .py-6 {
            padding-top: 0.75rem;
            padding-bottom: 0.75rem;
          }
        }

        /* Touch Device Optimizations */
        @media (hover: none) and (pointer: coarse) {
          /* Increase touch targets */
          button, input, select, textarea {
            min-height: 48px;
            min-width: 48px;
          }
          
          /* Better touch feedback */
          button:active, input:active, select:active {
            transform: scale(0.95);
            box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
            transition: all 0.1s ease;
          }
          
          /* Larger touch areas for table interactions */
          td, th {
            min-height: 48px;
            padding: 0.875rem 0.5rem;
          }
        }

        /* Device-Specific Optimizations */
        
        /* iPhone SE and similar small devices */
        @media (max-width: 375px) and (max-height: 667px) {
          .p-4 {
            padding: 0.5rem;
          }
          
          .py-4 {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
          }
          
          .text-2xl {
            font-size: 1.25rem;
          }
          
          .text-xl {
            font-size: 1.125rem;
          }
        }

        /* iPhone 12/13/14 Pro Max and similar large phones */
        @media (min-width: 428px) and (max-width: 480px) {
          .grid-cols-1 {
            grid-template-columns: 1fr;
          }
        }

        /* Samsung Galaxy Fold and similar foldables */
        @media (max-width: 280px) {
          .min-h-screen {
            padding: 0.25rem;
          }
          
          .text-2xl {
            font-size: 1.125rem;
          }
          
          .text-xl {
            font-size: 1rem;
          }
          
          .text-lg {
            font-size: 0.875rem;
          }
          
          button {
            font-size: 0.75rem;
            padding: 0.5rem 0.75rem;
          }
          
          table {
            font-size: 0.65rem;
          }
        }

        /* iPad and similar tablets in portrait */
        @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
          .grid-cols-1 {
            grid-template-columns: 1fr;
          }
        }

        /* iPad and similar tablets in landscape */
        @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
          .grid-cols-1 {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* High DPI Screens */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          /* Ensure crisp text on high DPI displays */
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        }

        /* Mobile Performance Optimizations */
        @media (max-width: 768px) {
          /* Optimize animations for mobile */
          .transform {
            will-change: transform;
          }
          
          /* Reduce shadow complexity on mobile */
          .shadow-lg, .shadow-xl {
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
          
          /* Optimize table rendering */
          table {
            contain: layout style paint;
          }
          
          /* Reduce gradient complexity */
          .bg-gradient-to-b {
            background: linear-gradient(to bottom, var(--tw-gradient-stops));
          }
        }

        /* Mobile Battery Optimization */
        @media (max-width: 768px) {
          /* Reduce unnecessary repaints */
          .hover\\:scale-105:hover {
            transform: scale(1.02);
          }
          
          /* Optimize transitions */
          .transition-all {
            transition: all 0.2s ease;
          }
          
          /* Reduce animation complexity */
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { 
              transform: translateY(10px);
              opacity: 0;
            }
            to { 
              transform: translateY(0);
              opacity: 1;
            }
          }
        }

        /* Mobile Network Optimization */
        @media (max-width: 768px) {
          /* Reduce image sizes for faster loading */
          img {
            max-width: 100%;
            height: auto;
          }
          
          /* Optimize font loading */
          * {
            font-display: swap;
          }
        }

        /* Mobile Gesture Support */
        @media (max-width: 768px) {
          /* Enable smooth scrolling */
          html {
            scroll-behavior: smooth;
          }
          
          /* Better touch scrolling */
          .overflow-x-auto {
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          
          .overflow-x-auto::-webkit-scrollbar {
            display: none;
          }
          
          /* Form scroll optimization */
          form {
            scroll-margin-top: 1rem;
          }
          
          /* Smooth scroll to form */
          .scroll-smooth {
            scroll-behavior: smooth;
          }
        }

        /* Dark Mode Support */
        @media (prefers-color-scheme: dark) {
          .bg-gradient-to-b.from-blue-50.to-gray-100 {
            background: linear-gradient(to bottom, #1e3a8a, #1f2937);
          }
          
          .bg-white {
            background-color: #374151;
          }
          
          .text-gray-800 {
            color: #f9fafb;
          }
          
          .text-gray-600 {
            color: #d1d5db;
          }
          
          .border-gray-200 {
            border-color: #4b5563;
          }
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Print Styles */
        @media print {
          .bg-gradient-to-b {
            background: white !important;
          }
          
          .shadow-lg, .shadow-xl {
            box-shadow: none !important;
          }
          
          table {
            border-collapse: collapse;
          }
          
          th, td {
            border: 1px solid #000;
            padding: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Instruction;